import { createContext, useContext, useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import localforage from "localforage";

import { useAppServices } from "hook/services";
import Loader from "examples/Loader";
import PrivacyPolicyModal from "./PrivacyPolicyModal";
import RemoveAccessModal from "./RemoveAccessModal";
import Maintainance from "components/Maintainance/Maintainance";

const AgencyContext = createContext();
AgencyContext.displayName = "AgencyContext";

function AgencyProvider({ children }) {
  const Service = useAppServices();

  const [agency, setAgency] = useState({});
  const [agencyConfig, setAgencyConfig] = useState({});
  const [loader, setLoader] = useState(true);

  const [model, setModel] = useState(false);
  const [accessModel, SetAccessModel] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [activities, setActivities] = useState([]);

  const agencyInterval = useRef(null);
  const agencyLoadedRef = useRef(false);
  const lastPayloadRef = useRef(null);

  // -------------------------------
  // UPDATE HELPERS
  // -------------------------------
  const Update = (updates) => {
    setAgency((prev) => ({ ...prev, ...updates }));
  };

  const UpdateAgencyConfig = (updates) => {
    setAgencyConfig((prev) => {
      const data = { ...prev, ...updates };
      localforage.setItem("agency_config", data);
      return data;
    });
  };

  const clear = () => setAgency({});

  const value = useMemo(
    () => [agency, Update, clear, agencyConfig, UpdateAgencyConfig, activities],
    [agency, agencyConfig, activities]
  );

  // -------------------------------
  // MAIN API
  // -------------------------------
  const getAgency = async (localAgency) => {
    try {
      const token = await localforage.getItem("token");

      const { response } = await Service.agency.get({
        query: `_id=${localAgency._id}`,
        token,
      });

      if (!response || !response.data) {
        // Only the first fetch may decide the agency is unreachable. Once a
        // session is up, a dropped poll must not swap the app for the
        // maintenance screen — that reads to the user as a random reload.
        if (!agencyLoadedRef.current) {
          setNotFound(true);
        }
        setLoader(false);
        return;
      }

      const data = response.data;
      agencyLoadedRef.current = true;

      // -------------------------------
      // PRIVACY POLICY CHECK
      // -------------------------------
      if (data?.ghl?.agency_app) {
        if (!data?.privacy_policy?.checked) {
          setModel(true);
        }
      } else {
        if (!data?.privacy_policy?.checked && data?.external_key) {
          setModel(true);
        }
      }

      // -------------------------------
      // ACCESS CHECK
      // -------------------------------
      if (!data?.access) {
        SetAccessModel(true);
      }

      // -------------------------------
      // SET DATA
      // -------------------------------
      const config = response.agency_config || {};
      const nextActivities = response?.activities || [];

      // Publishing unchanged data would hand every consumer new object
      // identities on each poll and re-render the whole app every 10s.
      const payload = JSON.stringify([data, config, nextActivities]);
      if (payload !== lastPayloadRef.current) {
        lastPayloadRef.current = payload;

        setAgency(data);
        setAgencyConfig(config);
        setActivities(nextActivities);

        await localforage.setItem("agency", data);
        await localforage.setItem("agency_config", config);
      }

      // -------------------------------
      // STOP POLLING IF DONE
      // -------------------------------
      if (data?.load_dummy_data && agencyInterval.current) {
        clearInterval(agencyInterval.current);
        agencyInterval.current = null;
      }
    } catch (error) {
      console.error("Agency fetch failed:", error);
    } finally {
      setLoader(false); // ✅ ONLY HERE (IMPORTANT FIX)
    }
  };

  // -------------------------------
  // INITIAL LOAD
  // -------------------------------
  const updateAgency = async () => {
    const localAgency = await localforage.getItem("agency");

    if (!localAgency) {
      setLoader(false);
      return;
    }

    // set local immediately (optional UI speed)
    setAgency(localAgency);

    // start polling if needed
    if (!localAgency?.load_dummy_data && !agencyInterval.current) {
      agencyInterval.current = setInterval(() => {
        getAgency(localAgency);
      }, 10000);
    }

    // ALWAYS wait API before removing loader
    await getAgency(localAgency);
  };

  useEffect(() => {
    updateAgency();

    return () => {
      if (agencyInterval.current) {
        clearInterval(agencyInterval.current);
      }
    };
  }, []);

  // -------------------------------
  // UI STATES
  // -------------------------------
  if (notFound) {
    return <Maintainance message="Agency not found or currently under maintenance." />;
  }

  if (loader) {
    return <Loader />;
  }

  return (
    <AgencyContext.Provider value={value}>
      
      {/* -------------------------------
          PRIVACY MODAL (BLOCK UI)
      ------------------------------- */}
      {model && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.65)",
            zIndex: 99999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "all",
          }}
        >
          <PrivacyPolicyModal
            open={model}
            onClose={() => setModel(false)}
            Update={Update}
            agency={agency}
            onPrivacy={() => {}}
          />
        </div>
      )}

      {/* ACCESS BLOCK */}
      {accessModel && (
        <RemoveAccessModal
          open={accessModel}
          onClose={() => SetAccessModel(false)}
          Update={Update}
          agency={agency}
        />
      )}

      {/* MAIN APP */}
      {!model && children}
    </AgencyContext.Provider>
  );
}

// -------------------------------
// HOOK
// -------------------------------
function useAgencyInfo() {
  return useContext(AgencyContext) || [];
}

AgencyProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AgencyProvider, useAgencyInfo };